/**
 * Email template management page.
 *
 * Lists managed templates, migration state, placeholder coverage, and opens
 * the shared draft/publish editor backed by /api/admin/notifications/templates.
 *
 * @copyright 2026 Glimbr Pty Ltd
 * @version 2026-04-21-v1
 */
import { useMemo, useState } from 'react';
import { AlertTriangle, Download, RefreshCw, Search, Upload } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Chip } from '../components/chip';
import { RoleButton } from '../components/role-gate';
import { useToast } from '../components/toast';
import { useAdminSession } from '../../lib/auth';
import {
    importNotificationTemplateLegacyExport,
    useNotificationKpis,
    useTemplates
} from '../../lib/api';
import { exportCsv } from '../../lib/csv';
import { apiGet, getHttpErrorMessage } from '../../lib/http';
import { appendAuditEntry } from '../../lib/mutations';
import type {
    TemplateDetail,
    TemplateImportSummaryRow,
    TemplatePerformance,
    Tone
} from '../../lib/types';
import { TemplateEditorModal } from './notifications-modals';

type StatusFilter = 'all' | 'ready' | 'review' | 'registry';

const STATUS_OPTIONS: { id: StatusFilter; label: string }[] = [
    { id: 'all', label: 'All' },
    { id: 'ready', label: 'Ready' },
    { id: 'review', label: 'Needs review' },
    { id: 'registry', label: 'Registry only' }
];

const MIGRATION_LABELS: Record<string, string> = {
    imported_ready: 'Imported ready',
    managed_ready: 'Managed ready',
    needs_helper_removal: 'Helper review',
    needs_placeholder_review: 'Placeholder review',
    legacy_fallback: 'Legacy fallback',
    registry_only: 'Registry only'
};

function templateTitle(template: TemplatePerformance) {
    return template.label || template.slug.replaceAll('_', ' ');
}

function isReady(template: TemplatePerformance) {
    return (
        template.ready_for_direct_send === true ||
        template.migration_state === 'imported_ready' ||
        template.migration_state === 'managed_ready'
    );
}

function needsReview(template: TemplatePerformance) {
    return (
        Number(template.unsupported_helper_count || 0) > 0 ||
        Number(template.missing_contract_count || 0) > 0 ||
        template.migration_state === 'needs_helper_removal' ||
        template.migration_state === 'needs_placeholder_review'
    );
}

function isRegistryOnly(template: TemplatePerformance) {
    return template.storage_state === 'fallback' || template.migration_state === 'registry_only';
}

function migrationTone(template: TemplatePerformance): Tone {
    if (needsReview(template)) return 'warning';
    if (isReady(template)) return 'success';
    if (isRegistryOnly(template)) return 'muted';
    return 'info';
}

function migrationLabel(template: TemplatePerformance) {
    const state = template.migration_state || 'registry_only';
    return MIGRATION_LABELS[state] || state.replaceAll('_', ' ');
}

function sourceLabel(template: TemplatePerformance) {
    if (template.published_version_source === 'sendgrid_import') return 'Bundled legacy export';
    if (template.published_version_source === 'admin') return 'Admin draft';
    if (template.storage_state === 'database') return 'Database';
    return 'Registry';
}

function formatDate(value?: string | null) {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
    });
}

function importSummaryLabel(row: TemplateImportSummaryRow) {
    if (row.status === 'imported') {
        return `v${row.version_number || '?'} ${row.published ? 'published' : 'draft'}`;
    }
    return row.reason || row.status;
}

export function EmailTemplatesPage() {
    const templates = useTemplates();
    const kpis = useNotificationKpis();
    const qc = useQueryClient();
    const session = useAdminSession();
    const { toast } = useToast();

    const [search, setSearch] = useState('');
    const [category, setCategory] = useState('all');
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
    const [activeTemplate, setActiveTemplate] = useState<TemplatePerformance | null>(null);
    const [importing, setImporting] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [lastImport, setLastImport] = useState<TemplateImportSummaryRow[] | null>(null);

    const rows = templates.data || [];
    const stats = useMemo(() => {
        const total = rows.length;
        const ready = rows.filter(isReady).length;
        const review = rows.filter(needsReview).length;
        const registry = rows.filter(isRegistryOnly).length;
        const published = rows.filter((row) => Boolean(row.published_version_id)).length;
        const placeholders = rows.reduce((sum, row) => sum + Number(row.placeholder_count || 0), 0);
        return { total, ready, review, registry, published, placeholders };
    }, [rows]);

    const categories = useMemo(
        () => Array.from(new Set(rows.map((row) => row.category).filter(Boolean))).sort(),
        [rows]
    );

    const filteredRows = useMemo(() => {
        const needle = search.trim().toLowerCase();
        return rows.filter((row) => {
            const haystack = [
                row.slug,
                row.label,
                row.category,
                row.description,
                row.legacy_template_id,
                row.migration_state,
                row.published_version_source
            ]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();
            if (needle && !haystack.includes(needle)) return false;
            if (category !== 'all' && row.category !== category) return false;
            if (statusFilter === 'ready' && !isReady(row)) return false;
            if (statusFilter === 'review' && !needsReview(row)) return false;
            if (statusFilter === 'registry' && !isRegistryOnly(row)) return false;
            return true;
        });
    }, [category, rows, search, statusFilter]);

    const statusCounts: Record<StatusFilter, number> = {
        all: rows.length,
        ready: stats.ready,
        review: stats.review,
        registry: stats.registry
    };

    async function refreshTemplates() {
        await Promise.all([
            qc.invalidateQueries({ queryKey: ['admin', 'notifications', 'templates'] }),
            qc.invalidateQueries({ queryKey: ['admin', 'notifications', 'kpis'] })
        ]);
        toast('Template data refreshed');
    }

    async function importLegacyExportTemplates() {
        if (importing) return;
        const confirmed = window.confirm(
            'Migrate the bundled legacy template export into managed template storage? Existing published versions will not be replaced.'
        );
        if (!confirmed) return;

        setImporting(true);
        try {
            const result = await importNotificationTemplateLegacyExport({
                source: 'bundled_export',
                replace_published: false
            });
            setLastImport(result.summary);
            await Promise.all([
                qc.invalidateQueries({ queryKey: ['admin', 'notifications', 'templates'] }),
                qc.invalidateQueries({ queryKey: ['admin', 'notifications', 'kpis'] })
            ]);
            const imported = result.summary.filter((row) => row.status === 'imported').length;
            const skipped = result.summary.length - imported;
            appendAuditEntry(qc, {
                actor: session.email,
                actor_role: session.role,
                action: 'notifications.templates.import_legacy_export',
                target_type: 'notification_template',
                target_id: 'all',
                severity: 'sensitive',
                reason: `${imported} imported, ${skipped} skipped`
            });
            toast('Legacy template migration complete', {
                kind: imported > 0 ? 'success' : 'info',
                detail: `${imported} imported, ${skipped} skipped`
            });
        } catch (error) {
            toast('Legacy template migration failed', {
                kind: 'danger',
                detail: getHttpErrorMessage(error, 'Template import failed')
            });
        } finally {
            setImporting(false);
        }
    }

    async function exportPlaceholderRegistry() {
        if (exporting || filteredRows.length === 0) return;
        setExporting(true);
        try {
            const details = await Promise.all(
                filteredRows.map((row) =>
                    apiGet<TemplateDetail>(`/api/admin/notifications/templates/${row.slug}`)
                )
            );
            exportCsv(
                `email-template-placeholders-${new Date().toISOString().slice(0, 10)}`,
                details.map((detail) => ({
                    slug: detail.slug,
                    label: detail.label,
                    category: detail.category,
                    migration_state: detail.migration_state,
                    storage_state: detail.storage_state,
                    legacy_template_id: detail.legacy_template_id || '',
                    supported_placeholders: detail.supported_placeholders.join(' '),
                    specific_placeholders: detail.specific_placeholders.join(' '),
                    detected_placeholders:
                        detail.published_version?.placeholder_analysis?.detected_placeholders?.join(
                            ' '
                        ) || ''
                })),
                [
                    'slug',
                    'label',
                    'category',
                    'migration_state',
                    'storage_state',
                    'legacy_template_id',
                    'supported_placeholders',
                    'specific_placeholders',
                    'detected_placeholders'
                ]
            );
            toast('Placeholder export downloaded', {
                kind: 'info',
                detail: `${details.length} template${details.length === 1 ? '' : 's'}`
            });
        } catch (error) {
            toast('Placeholder export failed', {
                kind: 'danger',
                detail: getHttpErrorMessage(error, 'Unable to export placeholders')
            });
        } finally {
            setExporting(false);
        }
    }

    const loading = templates.isLoading;
    const templateError =
        templates.error instanceof Error
            ? templates.error.message
            : templates.error
              ? String(templates.error)
              : '';

    return (
        <div className="stack-16">
            <header className="page-header">
                <div className="page-title-group">
                    <h1>Email templates</h1>
                    <p>Managed outbound email content, placeholders, and migration state.</p>
                </div>
                <div className="page-actions">
                    <button className="btn btn-ghost" onClick={refreshTemplates}>
                        <RefreshCw size={14} /> Refresh
                    </button>
                    <button
                        className="btn btn-ghost"
                        onClick={exportPlaceholderRegistry}
                        disabled={exporting || filteredRows.length === 0}
                    >
                        <Download size={14} /> {exporting ? 'Exporting...' : 'Export placeholders'}
                    </button>
                    <RoleButton
                        scope="owner_only"
                        className="btn btn-primary"
                        onClick={importLegacyExportTemplates}
                        disabled={importing}
                    >
                        <Upload size={14} />{' '}
                        {importing ? 'Migrating...' : 'Migrate legacy export'}
                    </RoleButton>
                </div>
            </header>

            {templateError && (
                <section className="alert-bar">
                    <div className="alert" data-severity="danger">
                        <AlertTriangle size={16} />
                        <span className="msg">{templateError}</span>
                        <span className="meta">templates</span>
                    </div>
                </section>
            )}

            {stats.registry > 0 && !templateError && (
                <section className="alert-bar">
                    <div className="alert" data-severity="warning">
                        <AlertTriangle size={16} />
                        <span className="msg">
                            {stats.registry} template{stats.registry === 1 ? '' : 's'} only have
                            registry metadata until managed storage is migrated and imported.
                        </span>
                        <span className="meta">migration</span>
                    </div>
                </section>
            )}

            <section className="kpi-grid kpi-grid-5" aria-label="Template metrics">
                <KpiMini
                    label="Templates"
                    value={String(kpis.data?.managed_templates ?? stats.total)}
                />
                <KpiMini label="Published" value={String(stats.published)} />
                <KpiMini
                    label="Direct send ready"
                    value={String(kpis.data?.direct_send_ready_templates ?? stats.ready)}
                />
                <KpiMini
                    label="Needs review"
                    value={String(kpis.data?.templates_needing_review ?? stats.review)}
                    alert={stats.review > 0}
                />
                <KpiMini label="Placeholders" value={String(stats.placeholders)} />
            </section>

            <section className="card">
                <header className="card-header email-template-card-header">
                    <h2 className="card-title">Template registry</h2>
                    <div className="email-template-count">
                        {filteredRows.length.toLocaleString()} of {rows.length.toLocaleString()}
                    </div>
                </header>

                <div className="email-template-toolbar">
                    <div className="filter-bar">
                        <label className="search" aria-label="Search templates">
                            <Search size={14} />
                            <input
                                value={search}
                                onChange={(event) => setSearch(event.target.value)}
                                placeholder="Search templates"
                            />
                        </label>
                        <select
                            className="select"
                            value={category}
                            onChange={(event) => setCategory(event.target.value)}
                            aria-label="Template category"
                        >
                            <option value="all">All categories</option>
                            {categories.map((item) => (
                                <option key={item} value={item}>
                                    {item}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="segment-tabs" role="tablist" aria-label="Template status">
                        {STATUS_OPTIONS.map((option) => (
                            <button
                                key={option.id}
                                className="segment-tab"
                                aria-selected={statusFilter === option.id}
                                onClick={() => setStatusFilter(option.id)}
                                type="button"
                            >
                                {option.label}
                                <span className="count">{statusCounts[option.id]}</span>
                            </button>
                        ))}
                    </div>
                </div>

                <div className="table-wrap email-template-table-wrap">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>Template</th>
                                <th>Category</th>
                                <th>Status</th>
                                <th>Source</th>
                                <th className="numeric">Placeholders</th>
                                <th>Legacy ID</th>
                                <th>Updated</th>
                                <th className="numeric">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={8} className="empty-state">
                                        Loading templates...
                                    </td>
                                </tr>
                            ) : filteredRows.length > 0 ? (
                                filteredRows.map((template) => (
                                    <tr
                                        key={template.slug}
                                        onClick={() => setActiveTemplate(template)}
                                        aria-selected={activeTemplate?.slug === template.slug}
                                    >
                                        <td>
                                            <div className="user-meta">
                                                <span className="user-name">
                                                    {templateTitle(template)}
                                                </span>
                                                <span className="user-sub mono">
                                                    {template.slug}
                                                </span>
                                                {template.description && (
                                                    <span className="sub">
                                                        {template.description}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <Chip tone="muted">{template.category}</Chip>
                                        </td>
                                        <td>
                                            <Chip tone={migrationTone(template)} dot>
                                                {migrationLabel(template)}
                                            </Chip>
                                        </td>
                                        <td>
                                            <div className="user-meta">
                                                <span className="user-name">
                                                    {sourceLabel(template)}
                                                </span>
                                                {template.published_version_number && (
                                                    <span className="user-sub">
                                                        v{template.published_version_number}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="numeric">
                                            {Number(
                                                template.placeholder_count || 0
                                            ).toLocaleString()}
                                            {Number(template.missing_contract_count || 0) > 0 && (
                                                <div className="sub text-warning">
                                                    {template.missing_contract_count} missing
                                                </div>
                                            )}
                                            {Number(template.unsupported_helper_count || 0) > 0 && (
                                                <div className="sub text-warning">
                                                    {template.unsupported_helper_count} helpers
                                                </div>
                                            )}
                                        </td>
                                        <td className="mono">
                                            {template.legacy_template_id || '-'}
                                        </td>
                                        <td>{formatDate(template.updated_at)}</td>
                                        <td className="numeric">
                                            <button
                                                className="btn btn-sm btn-ghost"
                                                type="button"
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    setActiveTemplate(template);
                                                }}
                                            >
                                                Edit
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={8} className="empty-state">
                                        No templates match the current filters.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </section>

            {lastImport && (
                <section className="card">
                    <header className="card-header">
                        <h2 className="card-title">Last legacy export migration</h2>
                    </header>
                    <div className="card-body tight">
                        {lastImport.map((row) => (
                            <div className="email-template-import-row" key={row.slug}>
                                <span className="mono">{row.slug}</span>
                                <Chip tone={row.status === 'imported' ? 'success' : 'muted'}>
                                    {row.status}
                                </Chip>
                                <span className="text-muted">{importSummaryLabel(row)}</span>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            <TemplateEditorModal
                template={activeTemplate}
                onClose={() => setActiveTemplate(null)}
            />
        </div>
    );
}

function KpiMini({ label, value, alert }: { label: string; value: string; alert?: boolean }) {
    return (
        <div className="kpi-tile" data-alert={alert ? 'true' : 'false'}>
            <span className="label">{label}</span>
            <span className="value">{value}</span>
        </div>
    );
}
